/**
 * One error shape for the whole platform. The API serialises `AppError` into
 * `{ error: { code, message, details } }`; the bot turns the same `code` into a
 * Khmer/English message via the i18n layer. Never throw bare strings.
 */

export type ErrorCode =
  // auth / tenancy
  | 'unauthenticated'
  | 'forbidden'
  | 'shop_not_found'
  | 'shop_suspended'
  | 'not_a_member'
  // input
  | 'validation_failed'
  | 'not_found'
  | 'conflict'
  | 'slug_taken'
  // catalog
  | 'product_unavailable'
  | 'variant_unavailable'
  | 'out_of_stock'
  // order
  | 'invalid_transition'
  | 'order_locked'
  | 'empty_cart'
  | 'currency_mismatch'
  | 'shipping_unavailable'
  // payment
  | 'payment_provider_unavailable'
  | 'payment_already_confirmed'
  | 'payment_expired'
  | 'unique_amount_exhausted'
  | 'khqr_config_missing'
  // delivery
  | 'carrier_unavailable'
  // billing
  | 'plan_required'
  | 'policy_not_accepted'
  | 'insufficient_credits'
  | 'quota_exceeded'
  // infra
  | 'rate_limited'
  | 'upstream_failed'
  | 'internal';

const STATUS_BY_CODE: Record<ErrorCode, number> = {
  unauthenticated: 401,
  forbidden: 403,
  shop_not_found: 404,
  shop_suspended: 403,
  not_a_member: 403,
  validation_failed: 422,
  not_found: 404,
  conflict: 409,
  slug_taken: 409,
  product_unavailable: 409,
  variant_unavailable: 409,
  out_of_stock: 409,
  invalid_transition: 409,
  order_locked: 409,
  empty_cart: 422,
  currency_mismatch: 422,
  shipping_unavailable: 422,
  payment_provider_unavailable: 503,
  payment_already_confirmed: 409,
  payment_expired: 410,
  unique_amount_exhausted: 503,
  khqr_config_missing: 422,
  carrier_unavailable: 503,
  plan_required: 402,
  // Not 403: the merchant is allowed, they just have a step left.
  policy_not_accepted: 409,
  insufficient_credits: 402,
  quota_exceeded: 429,
  rate_limited: 429,
  upstream_failed: 502,
  internal: 500,
};

export class AppError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details: Record<string, unknown> | undefined;
  /** True when retrying the exact same request could succeed later. */
  readonly retryable: boolean;

  constructor(
    code: ErrorCode,
    message?: string,
    options: { details?: Record<string, unknown>; cause?: unknown; retryable?: boolean } = {},
  ) {
    super(message ?? code, { cause: options.cause });
    this.name = 'AppError';
    this.code = code;
    this.status = STATUS_BY_CODE[code];
    this.details = options.details;
    this.retryable = options.retryable ?? this.status >= 500;
  }

  toJSON(): { error: { code: ErrorCode; message: string; details?: Record<string, unknown> } } {
    return {
      error: {
        code: this.code,
        message: this.message,
        ...(this.details ? { details: this.details } : {}),
      },
    };
  }
}

export function isAppError(value: unknown): value is AppError {
  return value instanceof AppError;
}

export function fail(
  code: ErrorCode,
  message?: string,
  details?: Record<string, unknown>,
): never {
  throw new AppError(code, message, details ? { details } : {});
}

/** Narrow an unknown catch value into something loggable. */
export function toErrorInfo(error: unknown): { message: string; stack?: string; code?: string } {
  if (isAppError(error)) {
    return { message: error.message, code: error.code, ...(error.stack ? { stack: error.stack } : {}) };
  }
  if (error instanceof Error) {
    return { message: error.message, ...(error.stack ? { stack: error.stack } : {}) };
  }
  return { message: String(error) };
}
